/* eslint-disable react-refresh/only-export-components --
   This is a hook module: it returns dialog JSX (the `dialogs` node) rather than
   being a refreshable component, so the only-components rule doesn't apply. */
import { lazy, Suspense, useState, type ReactNode } from "react";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { useIntl } from "react-intl";
import {
  CreateDocumentDocument,
  MyselfDocument,
  PowersDataDocument,
} from "@/graphql/generated/operations";
import { ExtractionDialog, type ExtractionFlags } from "@/components/save/ExtractionDialog";
import { LinkedInImportDialog } from "@/components/save/LinkedInImportDialog";
import { ProfileExtractionDialog } from "@/components/save/ProfileExtractionDialog";
import { ProfileYamlExportDialog } from "@/components/save/ProfileYamlExportDialog";
import { PowerProfilePdfExport } from "@/components/grow/PowerProfilePdfExport";
import { useMe } from "@/auth/use-me";
import { throwForValidationResult, userFacingGraphqlError } from "@/lib/graphql-error";
import { logApplicationFailure } from "@/lib/user-error";
import { formatDateTime } from "@/lib/format-date";
import { useLinkedinReturn } from "../useLinkedinReturn";
import type { ServiceAction } from "./service-actions";

const CvEditor = lazy(() =>
  import("@/components/cv/CvEditor").then((m) => ({ default: m.CvEditor })),
);

// UTF-8-safe base64 (btoa only handles Latin-1). Used for the JSON data export
// so non-ASCII profile content (umlauts, names) survives the round-trip.
function utf8ToBase64(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export type ExtractionDoc = {
  fileName?: string;
  id: string;
  name: string;
  groupId: string | null;
  createdAt?: string | null;
};

export interface UseServiceActionsOptions {
  // Documents the extraction dialog may pull from. The host supplies its
  // already-loaded set (Documents page) or loads its own (profile).
  docs: readonly ExtractionDoc[];
  groups: readonly { id: string; name: string }[];
  // True while `docs` is still being loaded, so the extraction dialog can say
  // "not yet" instead of "not there".
  docsLoading?: boolean;
  // Called after an action persisted data, so the host can refresh its lists.
  onChanged?: () => void;
  // Surfaces a user-facing error message (export failures); the dialogs handle
  // their own errors internally.
  onError?: (message: string) => void;
}

export interface UseServiceActions {
  runServiceAction: (action: ServiceAction) => void;
  // Open the CV editor directly — null creates a new draft, an id edits it.
  // (The "cv" action opens a new one; existing drafts reopen via this.)
  openCv: (id: string | null) => void;
  // Actions currently running; hosts disable the matching card and may swap
  // its tags for a progress label. Set-shaped so a new busy-capable action
  // doesn't grow the API by another boolean.
  busyActions: ReadonlySet<ServiceAction>;
  // Render this once in the host; it carries every in-place dialog.
  dialogs: ReactNode;
}

// Single source for the Documents Input/Output actions: dialog state, the data
// export, and the dialog rendering. Used by both the Documents page cards and
// the profile ServiceMenu so neither duplicates the wiring.
export function useServiceActions({
  docs,
  groups,
  docsLoading,
  onChanged,
  onError,
}: UseServiceActionsOptions): UseServiceActions {
  const intl = useIntl();
  const apollo = useApolloClient();
  const me = useMe();
  const [createDocument] = useMutation(CreateDocumentDocument);

  const [exportBusy, setExportBusy] = useState(false);
  const [yamlExportOpen, setYamlExportOpen] = useState(false);
  const [powerPdfOpen, setPowerPdfOpen] = useState(false);
  const [cvEditor, setCvEditor] = useState<{ id: string | null } | null>(null);
  const [extraction, setExtraction] = useState<{ title: string; flags: ExtractionFlags } | null>(null);
  // CV Extractor: review-first profile extraction (extractProfileFromDocument,
  // @adr SPARK-0026-profile-extraction-as-inline-tracked-changes-review@1).
  const [cvExtractOpen, setCvExtractOpen] = useState(false);
  const {
    returnMarker: linkedinReturn,
    setReturnMarker: setLinkedinReturn,
    open: linkedinOpen,
    setOpen: setLinkedinOpen,
  } = useLinkedinReturn();

  async function handleExportData() {
    if (exportBusy) return;
    if (!me.id) {
      onError?.(intl.formatMessage({ id: "service.page.errNotSignedInExport", defaultMessage: "Not signed in - export not possible." }));
      return;
    }
    setExportBusy(true);
    try {
      // Profile is required; Grow results are best-effort (a user may have no
      // assessments yet), so a missing Grow record never blocks the export.
      const [meRes, growRes] = await Promise.allSettled([
        apollo.query({ query: MyselfDocument, fetchPolicy: "network-only" }),
        apollo.query({ query: PowersDataDocument, fetchPolicy: "network-only" }),
      ]);
      const myself = meRes.status === "fulfilled" ? meRes.value.data?.myself : null;
      if (!myself) {
        onError?.(intl.formatMessage({ id: "service.page.errProfileLoad", defaultMessage: "Profile data could not be loaded." }));
        return;
      }
      const grow = growRes.status === "fulfilled" ? growRes.value.data : null;
      const now = new Date();
      const payload = {
        _export: {
          source: "smycles",
          type: "Personendaten (Profil + Grow)",
          exportedAt: now.toISOString(),
          note: "Persönliche Daten gemäß DSGVO Art. 20 (Recht auf Datenübertragbarkeit).",
        },
        profile: myself,
        grow,
      };
      const json = JSON.stringify(payload, null, 2);
      const stamp = now.toISOString().slice(0, 19).replace(/[:T]/g, "-");
      const fileName = `smycles-datenexport-${stamp}.json`;
      const res = await createDocument({
        variables: {
          input: {
            data: {
              name: `Datenexport ${formatDateTime(now, intl.locale)}`,
              person: { id: me.id },
              tags: ["Datenexport"],
              fileName,
              content: { mimeType: "application/json", content: utf8ToBase64(json) },
            },
            dryRun: false,
          },
        },
      });
      const out = res.data?.createDocument;
      if (out?.__typename !== "CreateDocumentResult") {
        throwForValidationResult(out, intl.formatMessage({ id: "service.page.errExportSave", defaultMessage: "Export could not be saved." }));
      }
      onChanged?.();
      // Hand the file to the user immediately, in addition to persisting it.
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      logApplicationFailure("save.service-export");
      onError?.(userFacingGraphqlError(intl, e));
    } finally {
      setExportBusy(false);
    }
  }

  const runServiceAction = (action: ServiceAction) => {
    switch (action) {
      case "power":
        setExtraction({
          title: intl.formatMessage({ id: "service.page.powerAnalysisTitle", defaultMessage: "Power analysis" }),
          flags: { extractPowers: true },
        });
        break;
      case "skills":
        setExtraction({
          title: intl.formatMessage({ id: "service.page.hardskillAnalysisTitle", defaultMessage: "Hard skill analysis" }),
          flags: { extractSkills: true },
        });
        break;
      case "linkedin":
        setLinkedinReturn(null);
        setLinkedinOpen(true);
        break;
      case "cv":
        setCvEditor({ id: null });
        break;
      case "cv-extract":
        setCvExtractOpen(true);
        break;
      case "power-pdf":
        setPowerPdfOpen(true);
        break;
      case "export":
        void handleExportData();
        break;
      case "yaml-export":
        setYamlExportOpen(true);
        break;
    }
  };

  const busyActions = new Set<ServiceAction>();
  if (exportBusy) busyActions.add("export");

  const dialogs = (
    <>
      {cvEditor ? (
        <Suspense fallback={null}>
          <CvEditor
            cvId={cvEditor.id}
            onClose={() => setCvEditor(null)}
            onSaved={() => onChanged?.()}
          />
        </Suspense>
      ) : null}

      {extraction ? (
        <ExtractionDialog
          title={extraction.title}
          flags={extraction.flags}
          docs={docs.map((d) => ({
            id: d.id,
            name: d.name,
            fileName: d.fileName,
            groupId: d.groupId,
            createdAt: d.createdAt ?? null,
          }))}
          groups={groups}
          docsLoading={docsLoading}
          personId={me.id ?? null}
          onClose={() => setExtraction(null)}
          onDone={() => onChanged?.()}
        />
      ) : null}

      {linkedinOpen ? (
        <LinkedInImportDialog
          returnMarker={linkedinReturn}
          onClose={() => {
            setLinkedinOpen(false);
            setLinkedinReturn(null);
          }}
        />
      ) : null}

      {cvExtractOpen ? (
        <ProfileExtractionDialog
          docs={docs}
          groups={groups}
          docsLoading={docsLoading}
          personId={me.id ?? null}
          onClose={() => setCvExtractOpen(false)}
          onDocumentSaved={() => onChanged?.()}
        />
      ) : null}

      {powerPdfOpen ? (
        <PowerProfilePdfExport onClose={() => setPowerPdfOpen(false)} onError={onError} />
      ) : null}

      {yamlExportOpen ? (
        <ProfileYamlExportDialog
          onClose={() => setYamlExportOpen(false)}
          onError={onError}
        />
      ) : null}
    </>
  );

  return { runServiceAction, openCv: (id) => setCvEditor({ id }), busyActions, dialogs };
}
